import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RichTextEditor, type UploadedAttachment } from '@/components/ui/rich-text-editor'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Check, Copy, X, FileIcon, ImageIcon } from 'lucide-react'
import { AiSparkle } from '@/components/AiSparkle'
import { recoverRequestsApi, type RunbookAttachment } from '../api'
import { useToast } from '@/hooks/use-toast'
import { getStatusPillClass, type RecoveryHeaderUpdate } from './RecoveryTrackingHeader'
import { sanitizeRichText } from '@/lib/sanitize-html'

/** Only user-selectable statuses; Assigned, Started, Unassigned are set by the application */
const ACTION_OPTIONS = ['InProgress', 'Completed', 'Issue'] as const

function stripHtml(html: string): string {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent?.trim() ?? ''
}

function formatDateShort(date: Date): string {
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
}

function formatFileSize(bytes: number | undefined): string {
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
}

interface AddRecoveryUpdateDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    requestID: number
    objectId: string
    objectDescription: string
    /** Optional extra information to show above the update area */
    otherInformation?: React.ReactNode
    updates: RecoveryHeaderUpdate[]
    tasks?: Array<{ taskNo?: number; task?: string; taskStatus?: string; waitGo?: string }>
    onSaved: () => void
}

export function AddRecoveryUpdateDialog({
    open,
    onOpenChange,
    requestID,
    objectId,
    objectDescription,
    otherInformation,
    updates,
    tasks = [],
    onSaved,
}: AddRecoveryUpdateDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const [action, setAction] = useState<string>('InProgress')
    const [updateHtml, setUpdateHtml] = useState<string>('')
    const [attachments, setAttachments] = useState<RunbookAttachment[]>([])
    const [saving, setSaving] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [copiedId, setCopiedId] = useState<string | null>(null)

    const copyUpdate = useCallback(async (message: string, id: string) => {
        const text = stripHtml(message) || message
        await navigator.clipboard.writeText(text)
        setCopiedId(id)
        setTimeout(() => setCopiedId(null), 2000)
        toast({ title: t('common.copied') || 'Copied', description: t('common.copiedToClipboard') || 'Copied to clipboard.' })
    }, [t, toast])

    const handleGenerate = useCallback(async () => {
        setGenerating(true)
        try {
            const res = await recoverRequestsApi.generateEngineerUpdate(requestID, {
                objectId,
                objectDescription,
                tasks: tasks.map(t => ({ taskNo: t.taskNo, task: t.task, taskStatus: t.taskStatus, waitGo: t.waitGo })),
                previousUpdates: updates.slice(0, 5).map(u => ({ action: u.action, message: u.message, dateTime: u.dateTime.toISOString() })),
            })
            const html = (res.data as any)?.data?.html || ''
            if (html) setUpdateHtml(html)
        } catch {
            toast({ title: 'Error', description: 'Failed to generate update', variant: 'destructive' })
        } finally {
            setGenerating(false)
        }
    }, [requestID, objectId, objectDescription, tasks, updates, toast])

    const handleAttach = useCallback((attachment: UploadedAttachment) => {
        setAttachments(prev => [...prev, attachment])
        toast({ title: t('common.attached') || 'Attached', description: attachment.name })
    }, [toast, t])

    const handleRemoveAttachment = useCallback((index: number) => {
        setAttachments(prev => prev.filter((_, i) => i !== index))
    }, [])

    const handleSave = async () => {
        setSaving(true)
        try {
            await recoverRequestsApi.addRecoveryAssignment(requestID, {
                action,
                update: updateHtml || '',
                attachments: attachments.length > 0 ? attachments : undefined,
            })
            toast({ title: t('common.saved') || 'Saved', description: 'Runbook update added.' })
            setUpdateHtml('')
            setAction('InProgress')
            setAttachments([])
            onSaved()
            onOpenChange(false)
        } catch (err: unknown) {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? (err as Error)?.message
            toast({ title: 'Error', description: msg || 'Failed to add update', variant: 'destructive' })
        } finally {
            setSaving(false)
        }
    }

    const handleOpenChange = (next: boolean) => {
        if (!next) { setUpdateHtml(''); setAction('InProgress'); setAttachments([]) }
        onOpenChange(next)
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col" aria-describedby="add-recovery-update-description">
                <DialogHeader>
                    <DialogTitle>{t('recoverRequests.addUpdate')}</DialogTitle>
                </DialogHeader>
                <div id="add-recovery-update-description" className="flex flex-1 min-h-0 flex-col gap-4 pt-2 overflow-y-auto">
                    {/* Object and other information above the update area */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-lg border border-gray-200 bg-gray-50/50 shrink-0">
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('recoverRequests.objectID')}</p>
                            <p className="text-sm font-medium text-gray-900 mt-0.5 break-all">{objectId || '—'}</p>
                        </div>
                        <div className="sm:col-span-1">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('recoverRequests.objectDescription')}</p>
                            <p className="text-sm text-gray-700 mt-0.5 line-clamp-2" title={objectDescription || undefined}>{objectDescription || '—'}</p>
                        </div>
                        <div className="sm:col-span-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('recoverRequests.otherInformation')}</p>
                            <div className="text-sm text-gray-600 mt-0.5">{otherInformation ?? t('recoverRequests.otherInformationPlaceholder')}</div>
                        </div>
                    </div>

                    {/* Other updates list */}
                    <div className="shrink-0">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('recoverRequests.otherUpdates')}</p>
                        <div className="max-h-32 overflow-y-auto space-y-2 rounded border border-gray-200 p-2 bg-white">
                            {updates.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2">{t('recoverRequests.noUpdatesYet')}</p>
                            ) : (
                                updates.map((u) => (
                                    <div key={u.id} className="group flex gap-2 text-sm border border-gray-100 rounded-lg p-2 bg-gray-50/50">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-xs text-muted-foreground">{formatDateShort(u.dateTime)}</span>
                                                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusPillClass(u.action)}`}>{u.action}</span>
                                            </div>
                                            <div
                                                className="prose prose-sm max-w-none mt-1 [&_p]:mb-0.5 [&_ul]:list-disc [&_ol]:list-decimal line-clamp-2 text-gray-700"
                                                dangerouslySetInnerHTML={{ __html: sanitizeRichText(u.message) }}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => copyUpdate(u.message, u.id)}
                                            className="shrink-0 p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors"
                                            title={t('common.copy')}
                                            aria-label={t('common.copy')}
                                        >
                                            {copiedId === u.id ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Update area */}
                    <div className="flex-1 min-w-0 flex flex-col gap-4 min-h-0">
                        <div className="space-y-2">
                            <Label htmlFor="recovery-update-action">{t('recoverRequests.action')}</Label>
                            <Select value={action} onValueChange={setAction}>
                                <SelectTrigger id="recovery-update-action" className="w-full">
                                    <SelectValue placeholder={t('recoverRequests.selectAction')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {ACTION_OPTIONS.map((opt) => (
                                        <SelectItem key={opt} value={opt}>
                                            {t(`recoverRequests.action${opt === 'InProgress' ? 'InProgress' : opt}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 flex-1 min-h-0 flex flex-col">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="recovery-update-html">{t('recoverRequests.update')}</Label>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="gap-1.5 h-7 text-xs"
                                    onClick={handleGenerate}
                                    disabled={generating}
                                >
                                    {generating ? (
                                        <img src="/images/vs-spinner.png" alt="" className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <AiSparkle className="h-5 w-5 text-blue-500" />
                                    )}
                                    {t('recoverRequests.aiGenerate', 'AI Generate')}
                                </Button>
                            </div>
                            <RichTextEditor
                                value={updateHtml}
                                onChange={setUpdateHtml}
                                placeholder={t('recoverRequests.updatePlaceholder')}
                                className="[&_.ProseMirror]:min-h-[160px] flex-1"
                                enableImageEmbed
                                enableAttachments
                                imageUploadDirectory="RunbookAssignments"
                                onAttach={handleAttach}
                            />
                        </div>

                        {/* Attachments list */}
                        {attachments.length > 0 && (
                            <div className="space-y-2">
                                <Label>{t('recoverRequests.attachments', 'Attachments')}</Label>
                                <div className="space-y-1">
                                    {attachments.map((att, i) => (
                                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded border bg-muted/50 text-sm">
                                            {att.type.startsWith('image/') ? (
                                                <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                                            ) : (
                                                <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                                            )}
                                            <a href={att.url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-primary hover:underline">
                                                {att.name}
                                            </a>
                                            {att.size && (
                                                <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(att.size)}</span>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveAttachment(i)}
                                                className="shrink-0 p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                                title="Remove"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter className="gap-2 sm:gap-0 shrink-0">
                    <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={saving}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="button" onClick={handleSave} disabled={saving}>
                        {saving ? (t('common.saving') || 'Saving...') : (t('common.save') || 'Save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
